import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import CommandPalette from "@/components/CommandPalette";
import { toast } from "sonner";
import { LayoutDashboard, Users, MessageSquare, Settings, LogOut, CalendarCheck, Receipt, FileText, Map, FileSignature, ClipboardList, Layers, Menu, Megaphone, Server, Cloud, Calculator, Smartphone, PlayCircle, Rocket, Trophy, Zap, PhoneCall, ClipboardCheck, Route, Wrench, DollarSign, Ticket, Wand2, MapPin, Package, IdCard, Palette, Star, Flag, AlertTriangle, Skull, Camera, ChevronDown, ChevronRight, ChevronUp, Info } from "lucide-react";

const LOGO_URL = "/logo-icon.png";

// Convert #RRGGBB → shadcn CSS-var `H S% L%` triple (space-separated).
// Used to hot-swap the app's primary color per-tenant from Settings.
function hexToHsl(hex) {
  const m = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (!m) return null;
  const r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Sidebar / mobile-menu top brand image.
 * Uses the current dealer's uploaded logo when Settings has one, else falls
 * back to the Decline Drive icon. Single source of truth for the header brand.
 */
function BrandLogo({ dealerInfo, className = "", testid }) {
  const dealerLogo = (dealerInfo?.dealership_logo_url || "").trim();
  const src = dealerLogo || LOGO_URL;
  const alt = dealerLogo
    ? dealerInfo?.dealership_name || "Dealership"
    : "Decline Drive — We Drive Further";
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      data-testid={testid}
      onError={(e) => {
        // fall back to DD icon if dealer URL is broken
        if (e.currentTarget.src !== window.location.origin + LOGO_URL) {
          e.currentTarget.src = LOGO_URL;
        }
      }}
    />
  );
}

const NAV_SECTIONS = [
  {
    title: "Dashboard",
    alwaysOpen: true,
    items: [
      { to: "/dashboard", label: "Overview / Advisors", icon: LayoutDashboard, testid: "nav-overview" },
      { to: "/appointments", label: "Appointments", icon: CalendarCheck, testid: "nav-appointments" },
      { to: "/line-items", label: "Declines by Service", icon: Wrench, testid: "nav-line-items" },
    ],
  },
  {
    title: "Pitch Flow",
    collapsible: true,
    defaultOpen: true,
    items: [
      { to: "/how-it-works", label: "How It Works", icon: PlayCircle, testid: "nav-how-it-works" },
      { to: "/pitch", label: "Live Pitch (4-Beat)", icon: Rocket, testid: "nav-pitch" },
      { to: "/customer-journey", label: "Customer Journey", icon: Route, testid: "nav-customer-journey" },
      { to: "/examples", label: "Example SMS Coupon", icon: Smartphone, testid: "nav-examples" },
    ],
  },
  {
    title: "Campaigns",
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/campaigns?t=prospect", label: "Fast Services", icon: Zap, testid: "nav-fast-services" },
      { to: "/conquest", label: "Conquest Customers", icon: MapPin, testid: "nav-conquest" },
    ],
  },
  {
    title: "Departments",
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/parts", label: "Parts Pipeline", icon: Package, testid: "nav-parts" },
      { to: "/body-shop", label: "Body Shop", icon: Palette, testid: "nav-body-shop" },
      {
        label: "PreOwned Pipeline",
        icon: Star,
        testid: "nav-posse-group",
        groupKey: "posse",
        children: [
          { to: "/posse", label: "Pipeline Board", icon: Star, testid: "nav-posse" },
          { to: "/recon-recovery", label: "Delayed Vehicles", icon: AlertTriangle, testid: "nav-recon-recovery" },
          { to: "/manager-checkin", label: "Manager Check-In", icon: ClipboardCheck, testid: "nav-manager-checkin" },
          { to: "/photo-pipeline", label: "Photo Pipeline", icon: Camera, testid: "nav-photo-pipeline" },
        ],
      },
    ],
  },
  {
    title: "Sales Tools",
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/sales-scorecard", label: "Sales Scorecard", icon: Trophy, testid: "nav-sales-scorecard" },
      { to: "/video-library", label: "Video Library", icon: PlayCircle, testid: "nav-video-library" },
      { to: "/decline-drive-letter", label: "Decline Drive Letter", icon: FileText, testid: "nav-dd-letter" },
      { to: "/one-pager", label: "One-Pager", icon: FileText, testid: "nav-one-pager" },
      { to: "/contract", label: "Contract", icon: FileSignature, testid: "nav-contract" },
      { to: "/statement", label: "Monthly Statement", icon: Receipt, testid: "nav-statement" },
      { to: "/business-card", label: "Business Card", icon: IdCard, testid: "nav-business-card" },
    ],
  },
  {
    title: "My Setup",
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
    ],
  },
];

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dealership, setDealership] = useState("");
  const [dealerInfo, setDealerInfo] = useState(null);

  // Rebrand-from-any-page state
  const [rebrandOpen, setRebrandOpen] = useState(false);
  const [rebrandName, setRebrandName] = useState("");
  const [rebranding, setRebranding] = useState(false);

  // Coupon-%-from-any-page state
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponDraft, setCouponDraft] = useState({ d1: 30, d2: 40, day1: 7, day2: 15 });
  const [couponSaving, setCouponSaving] = useState(false);

  // Sidebar dealer-info panel (collapsed by default — frees vertical space
  // on iPad so the nav list has room to breathe instead of ~1 inch of scroll)
  const [dealerPanelOpen, setDealerPanelOpen] = useState(false);
  const location = useLocation();

  // When the route changes, scroll the newly-active nav item into view so
  // the user isn't scroll-hunting from the previous page's scroll position.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="sidebar-nav"] [aria-current="page"]');
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const refreshSettings = async () => {
    try {
      const r = await api.get("/settings");
      setDealership(r.data?.dealership_name || "");
      setDealerInfo(r.data || null);
      setCouponDraft({
        d1: r.data?.stage_1_discount ?? 30,
        d2: r.data?.stage_2_discount ?? 40,
        day1: r.data?.stage_1_days ?? 7,
        day2: r.data?.stage_2_days ?? 15,
      });
    } catch (_) {}
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings")
      .then((r) => {
        if (cancelled) return;
        setDealership(r.data?.dealership_name || "");
        setDealerInfo(r.data || null);
        setCouponDraft({
          d1: r.data?.stage_1_discount ?? 30,
          d2: r.data?.stage_2_discount ?? 40,
          day1: r.data?.stage_1_days ?? 7,
          day2: r.data?.stage_2_days ?? 15,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const doRebrand = async () => {
    const name = rebrandName.trim();
    if (!name) return;
    setRebranding(true);
    try {
      await api.put("/settings", { dealership_name: name });
      await refreshSettings();
      toast.success(`App rebranded to ${name}`);
      setRebrandOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rebrand");
    } finally {
      setRebranding(false);
    }
  };

  const doCouponSave = async () => {
    setCouponSaving(true);
    try {
      await api.put("/settings", {
        stage_1_discount: Number(couponDraft.d1),
        stage_2_discount: Number(couponDraft.d2),
        stage_1_days: Number(couponDraft.day1),
        stage_2_days: Number(couponDraft.day2),
      });
      await refreshSettings();
      toast.success(
        `Coupon ladder saved · Day ${couponDraft.day1} = ${couponDraft.d1}% · Day ${couponDraft.day2} = ${couponDraft.d2}%`
      );
      setCouponOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setCouponSaving(false);
    }
  };

  const NavItems = ({ onClick }) => {
    const location = useLocation();
    const [openGroups, setOpenGroups] = useState(() => {
      const initial = {};
      NAV_SECTIONS.forEach((section) =>
        section.items.forEach((item) => {
          if (item.children && item.children.some((c) => location.pathname.startsWith(c.to))) {
            initial[item.groupKey] = true;
          }
        })
      );
      return initial;
    });
    // Section-level open state. Non-collapsible sections default open.
    const [openSections, setOpenSections] = useState(() => {
      const initial = {};
      NAV_SECTIONS.forEach((section) => {
        if (!section.collapsible) {
          initial[section.title] = true;
        } else {
          const hasActive = section.items.some((i) => i.to && location.pathname.startsWith(i.to));
          initial[section.title] = hasActive || !!section.defaultOpen;
        }
      });
      return initial;
    });
    const toggleGroup = (k) => setOpenGroups((prev) => ({ ...prev, [k]: !prev[k] }));
    const toggleSection = (title) => setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));

    const [platformAdmin, setPlatformAdmin] = useState(false);
    useEffect(() => {
      api.get("/platform/me").then((r) => setPlatformAdmin(!!r.data?.is_platform_admin)).catch(() => {});
    }, []);

    const renderLeaf = ({ to, label, icon: Icon, testid }, indented = false) => (
      <NavLink
        key={to}
        to={to}
        onClick={onClick}
        data-testid={testid}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
            indented ? "ml-4 pl-3 border-l border-border" : ""
          } ${
            isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
          }`
        }
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        <span>{label}</span>
      </NavLink>
    );

    return (
      <>
        {NAV_SECTIONS.map((section) => {
          const isCollapsible = !!section.collapsible;
          const isOpen = !!openSections[section.title];
          const SectionChevron = isOpen ? ChevronDown : ChevronRight;
          return (
            <div key={section.title} className="mb-4" data-testid={`nav-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
              {isCollapsible ? (
                <button
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  data-testid={`nav-section-toggle-${section.title.toLowerCase()}`}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-2 px-3 pb-1.5 hover:opacity-80"
                >
                  <SectionChevron className="w-3 h-3 text-[hsl(var(--gold))]" strokeWidth={2.5} />
                  <div className="font-mono-label text-[hsl(var(--gold))] text-[0.55rem] tracking-widest">
                    {section.title}
                  </div>
                </button>
              ) : (
                <div className="px-3 pb-1.5 font-mono-label text-[hsl(var(--gold))] text-[0.55rem] tracking-widest">
                  {section.title}
                </div>
              )}
              {isOpen && (
                <div className="space-y-1">
                  {section.items.map((item) => {
                    if (item.children) {
                      const isGroupOpen = !!openGroups[item.groupKey];
                      const hasActiveChild = item.children.some((c) => location.pathname.startsWith(c.to));
                      const GroupIcon = item.icon;
                      const Chevron = isGroupOpen ? ChevronDown : ChevronRight;
                      return (
                        <div key={item.groupKey} data-testid={`${item.testid}-wrap`}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(item.groupKey)}
                            data-testid={item.testid}
                            aria-expanded={isGroupOpen}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                              hasActiveChild
                                ? "bg-primary/10 text-foreground font-medium"
                                : "hover:bg-muted text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <GroupIcon className="w-4 h-4" strokeWidth={1.75} />
                            <span className="flex-1 text-left">{item.label}</span>
                            <Chevron className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                          {isGroupOpen && (
                            <div className="mt-1 space-y-1" data-testid={`${item.testid}-children`}>
                              {item.children.map((child) => renderLeaf(child, true))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return renderLeaf(item);
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Platform-owner admin link — only rendered when
            /platform/me flags is_platform_admin=true. */}
        {platformAdmin && (
          <div className="mb-4 pt-3 border-t border-border" data-testid="nav-section-platform">
            <div className="px-3 pb-1.5 font-mono-label text-[hsl(var(--gold))] text-[0.55rem] tracking-widest">
              Platform
            </div>
            <NavLink
              to="/platform"
              onClick={onClick}
              data-testid="nav-platform"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <Trophy className="w-4 h-4" strokeWidth={1.75} />
              <span>Platform Admin</span>
            </NavLink>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <CommandPalette />
      {/* Top copyright strip — removed. Copyright is in the footer only. */}

      {/* Dealership Name Banner — desktop only. On mobile the fixed top-bar
          already shows the dealer name + Sign out, so this banner is hidden
          to avoid the double-header stack that clipped content on iPhone. */}
      <div
        className="w-full bg-gradient-to-r from-[hsl(var(--gold))]/25 via-[hsl(var(--gold))]/10 to-[hsl(var(--gold))]/25 border-b-2 border-[hsl(var(--gold))] px-6 py-2.5 hidden md:flex items-center justify-between no-print"
        data-testid="dealer-banner"
      >
        <div className="flex items-baseline gap-3 min-w-0 flex-1">
          <div className="font-mono-label text-[hsl(var(--gold))] text-[0.6rem] shrink-0 hidden sm:block">
            Serving
          </div>
          <div
            className="font-display font-black text-lg sm:text-xl tracking-tight text-navy truncate"
            data-testid="dealer-banner-name"
          >
            {dealership || "Your Dealership"}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => {
              setRebrandName(dealership || "");
              setRebrandOpen(true);
            }}
            data-testid="banner-rebrand-btn"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-navy/70 hover:text-navy hover:bg-navy/10 text-[0.6rem] font-mono-label tracking-wide"
            title="One-tap rebrand — swap the dealership name across the app"
          >
            <Wand2 className="w-3 h-3" strokeWidth={2.2} />
            <span className="hidden sm:inline">Rebrand</span>
          </button>
          <div className="font-mono-label text-[0.55rem] sm:text-[0.6rem] text-navy/60 hidden md:block">
            powered by Decline Drive™
          </div>
        </div>
      </div>

      {/* Rebrand Dialog — reachable from every page */}
      <Dialog open={rebrandOpen} onOpenChange={setRebrandOpen}>
        <DialogContent data-testid="rebrand-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[hsl(var(--gold))]" strokeWidth={2} />
              One-Tap Rebrand
            </DialogTitle>
            <DialogDescription>
              Type the dealership&apos;s name — the sidebar, banners, One-Pager,
              Contract, and Statement all rebrand instantly.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={rebrandName}
            onChange={(e) => setRebrandName(e.target.value)}
            placeholder="e.g. Priority Auto Group"
            className="h-11 border-[hsl(var(--gold))]/60 focus-visible:ring-[hsl(var(--gold))]"
            data-testid="banner-rebrand-input"
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                await doRebrand();
              }
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRebrandOpen(false)}>Cancel</Button>
            <Button
              onClick={doRebrand}
              disabled={rebranding || !rebrandName.trim()}
              className="bg-[hsl(var(--gold))] text-neutral-900 hover:bg-[hsl(var(--gold-light))] font-bold"
              data-testid="banner-rebrand-confirm"
            >
              {rebranding ? "Rebranding…" : "Rebrand now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon % Dialog — quick edit stage_1/stage_2 discounts + days from anywhere */}
      <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
        <DialogContent data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Ticket className="w-5 h-5 text-emerald-600" strokeWidth={2} />
              Discount Ladder
            </DialogTitle>
            <DialogDescription>
              Change either offer's day or discount % — it flows through the Overview,
              Segments, Customer Journey, SMS Log, and every pitch page immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">Offer 1 — Day</div>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={couponDraft.day1}
                onChange={(e) => setCouponDraft({ ...couponDraft, day1: Number(e.target.value) })}
                data-testid="coupon-day1"
              >
                {[3,5,7,10,14,21,30].map((v) => <option key={v} value={v}>Day {v}</option>)}
              </select>
            </div>
            <div>
              <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">Offer 1 — Discount</div>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={couponDraft.d1}
                onChange={(e) => setCouponDraft({ ...couponDraft, d1: Number(e.target.value) })}
                data-testid="coupon-d1"
              >
                {[10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,90].map((v) => <option key={v} value={v}>{v}% off</option>)}
              </select>
            </div>
            <div>
              <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">Offer 2 — Day</div>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={couponDraft.day2}
                onChange={(e) => setCouponDraft({ ...couponDraft, day2: Number(e.target.value) })}
                data-testid="coupon-day2"
              >
                {[7,10,14,15,21,30,45,60].map((v) => <option key={v} value={v}>Day {v}</option>)}
              </select>
            </div>
            <div>
              <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">Offer 2 — Discount</div>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={couponDraft.d2}
                onChange={(e) => setCouponDraft({ ...couponDraft, d2: Number(e.target.value) })}
                data-testid="coupon-d2"
              >
                {[10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,90].map((v) => <option key={v} value={v}>{v}% off</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 rounded-md bg-muted/50 border border-border p-3 text-xs">
            <span className="font-semibold">Preview:</span> Day {couponDraft.day1} → {couponDraft.d1}% off ·{" "}
            Day {couponDraft.day2} → {couponDraft.d2}% off
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponOpen(false)}>Cancel</Button>
            <Button
              onClick={doCouponSave}
              disabled={couponSaving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="banner-coupon-confirm"
            >
              {couponSaving ? "Saving…" : "Save & flow everywhere"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex-1 flex">
      {/* Mobile top bar — logo + hamburger + sign out visible on small screens */}
      <header className="md:hidden fixed top-16 inset-x-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" strokeWidth={2} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 flex flex-col">
              <div className="px-4 py-4 border-b border-border bg-navy shrink-0">
                <BrandLogo
                  dealerInfo={dealerInfo}
                  className="w-full h-auto max-h-24 object-contain"
                />
              </div>
              <nav className="flex-1 min-h-0 py-4 px-2 space-y-1 overflow-y-auto overscroll-contain">
                <NavItems onClick={() => setMobileNavOpen(false)} />
              </nav>
            </SheetContent>
          </Sheet>
          <div className="p-0.5 bg-[hsl(var(--gold))] rounded-md">
            <BrandLogo
              dealerInfo={dealerInfo}
              className="w-7 h-7 object-contain rounded bg-white"
            />
          </div>
          <div className="font-display font-black text-sm text-[#0f2b6b] dark:text-blue-200">
            {dealerInfo?.dealership_name || "Decline Drive"}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="mobile-logout-btn"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
        >
          <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
          Sign out
        </Button>
      </header>

      {/* Sidebar — sticky full-viewport height so long nav lists stay scrollable */}
      <aside className="hidden md:flex md:flex-col w-60 border-r border-border bg-card sticky top-0 h-screen self-start">
        <div className="px-2 py-2 border-b border-border bg-navy shrink-0">
          <BrandLogo
            dealerInfo={dealerInfo}
            className="w-full h-auto max-h-24 object-contain"
            testid="brand-logo"
          />
        </div>
        <nav className="flex-1 min-h-0 py-3 px-2 space-y-1 overflow-y-auto overscroll-contain" data-testid="sidebar-nav">
          <NavItems />
        </nav>

        {/* Collapsed footer — one thin bar with dealer name + Sign out.
            Tap the chevron to reveal Current Dealer / Motto / Copyright. */}
        <div className="border-t border-border shrink-0" data-testid="sidebar-footer">
          <button
            type="button"
            onClick={() => setDealerPanelOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors"
            aria-expanded={dealerPanelOpen}
            data-testid="sidebar-dealer-toggle"
          >
            <Info className="w-3.5 h-3.5 text-[hsl(var(--gold))] shrink-0" strokeWidth={2} />
            <div className="min-w-0 flex-1 text-left">
              <div className="font-mono-label text-[0.5rem] text-muted-foreground tracking-widest leading-none">
                Dealer info
              </div>
              <div className="text-[0.7rem] font-medium text-foreground truncate leading-tight" title={dealerInfo?.dealership_name}>
                {dealerInfo?.dealership_name || "—"}
              </div>
            </div>
            {dealerPanelOpen
              ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={2} />
              : <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={2} />}
          </button>

          {dealerPanelOpen && (
            <div className="px-3 pb-3 max-h-[45vh] overflow-y-auto" data-testid="sidebar-dealer-panel">
              {dealerInfo && (dealerInfo.dealership_name || dealerInfo.service_director_name) && (
                <div className="rounded-md border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 p-3" data-testid="sidebar-dealer-info">
                  <div className="font-mono-label text-[hsl(var(--gold))] text-[0.55rem] mb-1.5">Current Dealer</div>
                  <div className="font-display font-bold text-sm text-navy dark:text-blue-200 leading-tight truncate" title={dealerInfo.dealership_name}>
                    {dealerInfo.dealership_name || "—"}
                  </div>
                  {dealerInfo.dealership_phone && (
                    <a href={`tel:${dealerInfo.dealership_phone}`} className="text-[0.65rem] text-foreground/70 hover:text-primary block truncate" data-testid="sidebar-dealer-phone">
                      {dealerInfo.dealership_phone}
                    </a>
                  )}
                  {dealerInfo.dealer_address && (
                    <div className="text-[0.6rem] text-muted-foreground mt-1 whitespace-pre-line line-clamp-2 leading-tight" data-testid="sidebar-dealer-address">
                      {dealerInfo.dealer_address}
                    </div>
                  )}
                  {(dealerInfo.service_director_name || dealerInfo.service_manager_name) && (
                    <div className="mt-2 pt-2 border-t border-[hsl(var(--gold))]/25 space-y-1">
                      {dealerInfo.service_director_name && (
                        <div>
                          <div className="font-mono-label text-muted-foreground text-[0.55rem]">Svc Director</div>
                          <div className="text-[0.7rem] font-medium truncate" data-testid="sidebar-sd-name">{dealerInfo.service_director_name}</div>
                          {dealerInfo.service_director_phone && (
                            <a href={`tel:${dealerInfo.service_director_phone}`} className="text-[0.6rem] text-muted-foreground hover:text-primary">
                              {dealerInfo.service_director_phone}
                            </a>
                          )}
                        </div>
                      )}
                      {dealerInfo.service_manager_name && (
                        <div>
                          <div className="font-mono-label text-muted-foreground text-[0.55rem]">Svc Manager</div>
                          <div className="text-[0.7rem] font-medium truncate" data-testid="sidebar-sm-name">{dealerInfo.service_manager_name}</div>
                          {dealerInfo.service_manager_phone && (
                            <a href={`tel:${dealerInfo.service_manager_phone}`} className="text-[0.6rem] text-muted-foreground hover:text-primary">
                              {dealerInfo.service_manager_phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {dealerInfo.dms_platform && (
                    <div className="mt-2 pt-2 border-t border-[hsl(var(--gold))]/25">
                      <div className="font-mono-label text-muted-foreground text-[0.55rem]">DMS</div>
                      <div className="text-[0.7rem] font-medium truncate" data-testid="sidebar-dms">{dealerInfo.dms_platform}</div>
                      {dealerInfo.dms_method && (
                        <div className="text-[0.6rem] text-muted-foreground truncate italic">{dealerInfo.dms_method}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 rounded-md bg-navy/5 dark:bg-navy/20 border border-navy/20 dark:border-blue-900 px-3 py-2.5" data-testid="sidebar-mantra">
                <div className="font-mono-label text-[0.55rem] text-[hsl(var(--gold))] tracking-widest mb-1">
                  MOTTO
                </div>
                <div className="text-[0.7rem] leading-snug text-navy dark:text-blue-100 italic font-medium">
                  &quot;The good thing is we&apos;re always improving — and you want it right, so we keep moving.&quot;
                </div>
              </div>
              <div className="mt-2 text-[0.6rem] text-muted-foreground leading-relaxed" data-testid="copyright-notice">
                © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises
              </div>
            </div>
          )}

          {/* Sign-in row — always visible at the very bottom */}
          <div className="px-3 py-2 border-t border-border">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-mono-label text-[0.5rem] text-muted-foreground tracking-widest leading-none">
                  Signed in
                </div>
                <div className="text-[0.7rem] font-medium truncate leading-tight" data-testid="current-user-email">{user?.email}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 shrink-0"
                data-testid="logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="sr-only">Sign out</span>
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main — overflow-x-hidden prevents any child (a wide table, a long
          headline, an input placeholder) from pushing the whole page into
          horizontal scroll on iPhone. Tables retain their own inner scroll. */}
      <main className="flex-1 min-w-0 pt-[7.5rem] md:pt-0 overflow-x-hidden">
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-10">{children}</div>
      </main>
      </div>
    </div>
  );
}
